/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { faFilter } from "@fortawesome/pro-regular-svg-icons";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import useConfig from "../../../../actions/Tenants/config/configHook";
import { useLayerPredictions } from "../../../../hooks";
import { LocationPrediction } from "../../../../hooks/useFacilityLocationQueries";
import { useConfiguredLayerChecks } from "../../../../hooks/useLayerChecks";
import Plot from "../../../../plotly/Plot";
import { getFilledArrayOrDefault, isFilledArray } from "../../../../utils";
import { toLocalGraphTime } from "../../../../utils/date";
import { Button } from "../../../Helper/Button";
import { Loading } from "../../../Helper/Loading";
import { attributeIsSet } from "../../../scenes/Tenants/Forms/flagConstants";
import { RIPENING_GRAPH_STYLES, ripeningGraphXAxis } from "../../Ripening/RipeningRoom/ripeningGraphConstants";


import { useRouteLayer, useRouteLayerQuery } from "../../../../hooks/useCurrentEntities";

function onlyUnique<T>(value: T, index: number, self: T[]): boolean {
    return self.indexOf(value) === index;
}

const isValidNumber = (number: any): boolean => (number > 0 || parseFloat(number) === 0) && number !== "" && number !== null && number !== undefined;

// Conversion functions matching the data scientist's approach
// to_log_space: np.log1p(pressure * 3)
function toLogSpace(pressure: number): number {
    return Math.log1p(pressure * 3);
}

// to_linear_space: np.expm1(log_value) / 3
function toLinearSpace(logValue: number): number {
    return (Math.expm1(logValue)) / 3;
}


export default function LayerRipeningGraph() {
    const layer = useRouteLayer();
    const isLayerLoading = useRouteLayerQuery().isLoading;
    const config = useConfig();
    const layerConfig = config.get_layer_config(layer);
    const { data: checks, isLoading: areChecksLoading } = useConfiguredLayerChecks(layer?.tenant_id, layer, layerConfig);
    const { data: layerPredictions = [] } = useLayerPredictions(layer?.tenant_id, layer?.id, Boolean(layer?.id));

    if (isLayerLoading || areChecksLoading || !checks) {
        return <Loading />;
    }
    return <RipeningGraph object_id={layer?.id as string} series={[checks]} fruit_type={layer?.fruit_type} predictions={layerPredictions} />;
}

export function RipeningGraph({
    object_id,
    series,
    fruit_type,
    title = true,
    minHeight = "45rem",
    predictions = [],
    seriesLayerIds = [],
}: {
    object_id: string;
    series: any[];
    fruit_type: string;
    title?: boolean | string;
    minHeight?: string;
    predictions?: LocationPrediction[];
    seriesLayerIds?: (string | undefined)[];
}) {
    const config = useConfig();
    const [ripeningFieldIndex, setRipeningFieldIndex] = useState(0);
    const [ignoreCheckLocations, setIgnoreCheckLocations] = useState([] as string[]);
    const [isLogScale, setIsLogScale] = useState(true);

    useEffect(() => {
        setIgnoreCheckLocations([]);
    }, [object_id, fruit_type]);

    useEffect(() => {
        setRipeningFieldIndex(0);
    }, [object_id, fruit_type]);

    const fruit_config = config.get_fruit_type(fruit_type);

    const the_field = fruit_config?.ripening_fields?.[ripeningFieldIndex];

    useEffect(() => {
        if (the_field) {
            setIsLogScale(the_field?.default_log_scale || fruit_type === "avocado");
        }
    }, [the_field?.default_log_scale]);

    // React Rules of Hooks: this useMemo must run on every render, so it stays
    // above the early return below. Guarding downstream logic separately is
    // fine because those are plain computations, not hook calls.
    const predictionsByLayer = useMemo(() => {
        if (!predictions?.length) {
            return {};
        }
        return predictions.reduce<Record<string, LocationPrediction[]>>((acc, prediction) => {
            if (!prediction?.layer_id) {
                return acc;
            }
            const layerKey = String(prediction.layer_id);
            if (!acc[layerKey]) {
                acc[layerKey] = [];
            }
            acc[layerKey].push(prediction);
            return acc;
        }, {});
    }, [predictions]);

    if (!the_field?.fieldname_getter) {
        return null;
    }
    const upper_bound_field = `${the_field.fieldname_getter}_upper_bound`;
    const lower_bound_field = `${the_field.fieldname_getter}_lower_bound`;

    const checkLocations = series.flatMap((i) => i.map((j: any) => j.location)).filter(onlyUnique).map((location: any) => ({
        value: location,
        label: config.get_raw_check_location(location)?.text || location,
    }));

    const sortedSeries = series.map((serie: any) => {
        const ordered = serie.filter((i: any) => isValidNumber(i[the_field.fieldname_getter]))
            .filter((i: any) => !ignoreCheckLocations.includes(i.location))
            .sort((a: any, b: any) => dayjs(a.created).valueOf() - dayjs(b.created).valueOf())
            .map((i: any) => {
                const baseValue = i[the_field.fieldname_getter];
                const upperBound = i[upper_bound_field];
                const lowerBound = i[lower_bound_field];

                return {
                    ...i,
                    location_label: i.location ? config.get_raw_check_location(i.location)?.text || i.location : "",
                    // Convert to log space when log scale is enabled
                    [the_field.fieldname_getter]: isLogScale && isValidNumber(baseValue)
                        ? toLogSpace(Number(baseValue))
                        : baseValue,
                    [upper_bound_field]: isLogScale && isValidNumber(upperBound)
                        ? toLogSpace(Number(upperBound))
                        : upperBound,
                    [lower_bound_field]: isLogScale && isValidNumber(lowerBound)
                        ? toLogSpace(Number(lowerBound))
                        : lowerBound,
                };
            });
        return ordered;
    });

    const predictedRangeValues: number[] = [];

    // Collect all bands and lines separately to ensure proper rendering order
    const allBands: any[] = [];
    const allLines: any[] = [];

    sortedSeries.forEach((serie: any, serieIndex: number) => {
        if (!serie.length) {
            return;
        }

        const created_array = serie.map((i: any) => toLocalGraphTime(i.created));
        const values_array = serie.map((i: any) => i[the_field.fieldname_getter]);
        const locations_array = serie.map((i: any) => i.location_label);

        // Convert log values to linear for hover display when in log scale
        const linearValuesForHover = isLogScale
            ? values_array.map((v: any) => toLinearSpace(Number(v)))
            : undefined;

        const baseLine = config_scatter_serie(
            created_array,
            values_array,
            RIPENING_GRAPH_STYLES.colors.actualLine,
            the_field.label,
            locations_array,
            isLogScale,
            linearValuesForHover,
        );

        const layerId = seriesLayerIds?.[serieIndex]
            ?? serie?.[0]?.layer_id
            ?? serie?.[0]?.layerId
            ?? (Array.isArray(serie?.[0]?.layer_ids) ? serie?.[0]?.layer_ids[0] : undefined);

        let predictionGraphData: PredictionGraphData | null = null;

        if (layerId && predictionsByLayer[String(layerId)]?.length) {
            const lastIndex = serie.length - 1;
            const lastMeasurement = serie[lastIndex];
            // `serie` is already converted to log space when `isLogScale` is true.
            // `buildPredictionGraphData` expects the last actual value in *linear* space
            // (it applies the log transform itself when `isLogScale` is enabled).
            const lastActualValueFromSerie = lastMeasurement?.[the_field.fieldname_getter];
            const lastActualValueLinear = isLogScale && isValidNumber(lastActualValueFromSerie)
                ? toLinearSpace(Number(lastActualValueFromSerie))
                : lastActualValueFromSerie;
            predictionGraphData = buildPredictionGraphData({
                predictions: predictionsByLayer[String(layerId)],
                fieldname: the_field.fieldname_getter,
                lastActualTimestamp: created_array[lastIndex],
                lastActualValue: lastActualValueLinear,
                isLogScale,
            });
        }

        let predictionLine: any[] = [];
        let predictionArea: any[] = [];

        const actualBandPoints = serie.reduce((acc: { x: string; upper: number; lower: number }[], point, pointIndex) => {
            const upper = point?.[upper_bound_field];
            const lower = point?.[lower_bound_field];
            if (isValidNumber(upper) && isValidNumber(lower)) {
                acc.push({
                    x: created_array[pointIndex],
                    upper: Number(upper),
                    lower: Number(lower),
                });
            }
            return acc;
        }, []);

        if (predictionGraphData && predictionGraphData.lineX.length > 1) {
            predictedRangeValues.push(...predictionGraphData.lineY);
            predictedRangeValues.push(...predictionGraphData.bandUpper);
            predictedRangeValues.push(...predictionGraphData.bandLower);

            // Convert log values to linear for hover display when in log scale
            const predictionLinearValues = isLogScale
                ? predictionGraphData.lineY.map(toLinearSpace)
                : predictionGraphData.lineY;

            predictionLine = [{
                type: "scatter",
                x: predictionGraphData.lineX,
                y: predictionGraphData.lineY,
                mode: "lines+markers",
                name: `${the_field.label} (Predicted)`,
                marker: {
                    color: RIPENING_GRAPH_STYLES.colors.predictedLine,
                    size: RIPENING_GRAPH_STYLES.marker.size,
                },
                line: {
                    color: RIPENING_GRAPH_STYLES.colors.predictedLine,
                    width: RIPENING_GRAPH_STYLES.lineWidth.predicted,
                },
                showlegend: false,
                hovertemplate: `<b>${the_field.label} (Predicted)</b><br>`
                              + `Value: ${isLogScale ? "%{customdata:.1f}" : "%{y:.2f}"}<br>`
                              + `Date: %{x}<br>`
                              + `<extra></extra>`,
                customdata: isLogScale ? predictionLinearValues : undefined,
            }];

            const predictionBandX = [...predictionGraphData.bandX];
            const predictionBandUpper = [...predictionGraphData.bandUpper];
            const predictionBandLower = [...predictionGraphData.bandLower];

            const lastActualBandPoint = actualBandPoints[actualBandPoints.length - 1];
            if (lastActualBandPoint) {
                predictionBandX.unshift(lastActualBandPoint.x);
                predictionBandUpper.unshift(lastActualBandPoint.upper);
                predictionBandLower.unshift(lastActualBandPoint.lower);
            }

            if (predictionBandX.length >= 2) {
                predictionArea = [{
                    type: "scatter",
                    x: [
                        ...predictionBandX,
                        ...predictionBandX.slice().reverse(),
                    ],
                    y: [
                        ...predictionBandUpper,
                        ...predictionBandLower.slice().reverse(),
                    ],
                    fill: "toself",
                    fillcolor: RIPENING_GRAPH_STYLES.colors.predictedBand,
                    line: { color: "rgba(255,255,255,0)", width: 0 },
                    hoverinfo: "skip",
                    showlegend: false,
                    opacity: RIPENING_GRAPH_STYLES.bandOpacity,
                    mode: "lines", // Explicitly set mode to ensure proper rendering
                }];
            }
        }

        let actualBandTrace: any[] = [];
        if (actualBandPoints.length >= 2) {
            const actualBandX = actualBandPoints.map((point) => point.x);
            const actualUpper = actualBandPoints.map((point) => point.upper);
            const actualLower = actualBandPoints.map((point) => point.lower);
            actualBandTrace = [{
                type: "scatter",
                x: [...actualBandX, ...actualBandX.slice().reverse()],
                y: [...actualUpper, ...actualLower.slice().reverse()],
                fill: "toself",
                fillcolor: RIPENING_GRAPH_STYLES.colors.actualBand,
                line: { color: "rgba(255,255,255,0)", width: 0 },
                hoverinfo: "skip",
                showlegend: false,
                opacity: RIPENING_GRAPH_STYLES.bandOpacity,
                mode: "lines", // Explicitly set mode to ensure proper rendering
            }];
        }

        // Collect bands first, then lines
        allBands.push(...predictionArea, ...actualBandTrace);
        allLines.push(...predictionLine, baseLine);
    });

    // Combine: all bands first (render behind), then all lines (render on top)
    const graphs = [...allBands, ...allLines];


    // Range values are already in log space if isLogScale is true
    const actualRangeValues = sortedSeries.flatMap((serie: any) => serie.flatMap((i: any) => [
        Number(i[the_field.fieldname_getter]),
        Number(i[upper_bound_field]),
        Number(i[lower_bound_field]),
    ]).filter((value) => isValidNumber(value)));

    const combinedRangeValues = [
        ...actualRangeValues,
        ...predictedRangeValues.filter((value) => isValidNumber(value)),
    ];

    const maxRangeValue = combinedRangeValues.length ? Math.max(...combinedRangeValues, 0) : 0;
    const range = [0, maxRangeValue > 0 ? Math.ceil(maxRangeValue * 1.06) : 1];

    // Generate tick values for log scale (in log space, but labels will show linear values)
    function generateTickValues(maxLogValue: number): number[] {
        // Generate reasonable tick values in log space
        // Convert some linear values to log space for ticks
        const linearTicks = [0.5, 1, 2, 3, 5, 10, 15, 20, 30, 40, 50];
        const logTicks = linearTicks.map(toLogSpace).filter((v) => v <= maxLogValue && v >= 0);
        // Always include 0 and max value
        if (logTicks.length === 0) {
            return [0, Math.max(0.1, maxLogValue)];
        }
        // Ensure we have a reasonable spread
        const result = [0, ...logTicks];
        if (!result.includes(maxLogValue)) {
            result.push(maxLogValue);
        }
        return result.sort((a, b) => a - b);
    }


    const layout = {
        autosize: true,
        showlegend: false,
        dragmode: false,
        hovermode: "closest",
        plot_bgcolor: "white", // Match backend: plot_bgcolor="white"
        paper_bgcolor: "white", // Match backend style
        margin: {
            l: 50,
            r: 10,
            t: 0,
            b: 50,
            pad: 0
        },
        yaxis: {
            fixedrange: true,
            title: the_field.label,
            showgrid: false,
            // Use linear scale - values are already converted to log space when needed
            type: "linear",
            range,
            autorange: the_field.higher_value_is_more_ripe ? "reversed" : false,
            // Custom tick formatting for log scale: show linear labels
            ...(isLogScale ? {
                tickmode: "array",
                tickvals: generateTickValues(range[1]),
                ticktext: generateTickValues(range[1]).map((v) => toLinearSpace(v).toFixed(1)),
            } : {}),
        },
        xaxis: ripeningGraphXAxis(graphs),
        shapes: [],
        annotations: [],
    };

    // * add categories as swimming lanes
    const filteredCategories = getFilledArrayOrDefault(the_field.categories).filter((i: any) => (attributeIsSet(i.gte) || attributeIsSet(i.gt)) && (attributeIsSet(i.lte) || attributeIsSet(i.lt)));
    if (isFilledArray(filteredCategories)) {
        // * add swimming lanes per category
        const categories = filteredCategories.map((i: any) => {
            const cat = ({
                ...i,
                label: i.label,
                lower: parseFloat(i.gt || i.gte || 0),
                upper: parseFloat(i.lt || i.lte || 100),
            });
            // Convert to log space using the same conversion as data scientist
            if (isLogScale) {
                cat.log_upper = toLogSpace(cat.upper);
                cat.log_lower = toLogSpace(Math.max(0.1, cat.lower)); // Use 0.1 minimum to prevent issues
            } else {
                cat.log_upper = cat.upper;
                cat.log_lower = cat.lower;
            }
            cat.middle = (cat.lower + cat.upper) / 2;
            cat.log_middle = (cat.log_lower + cat.log_upper) / 2;
            return cat;

        });


        // Add swimming lanes to the graph layout
        const swimmingLanes = categories.map((category) => ({
            type: "rect",
            x0: 0, // * Start at the left border
            x1: 1, // * End at the right border
            xref: "paper", // * Makes x0 and x1 relative to the entire plot width
            yref: "y",
            // Use log space values for y positions when in log scale
            y0: isLogScale ? category.log_lower : category.lower,
            y1: isLogScale ? category.log_upper : category.upper,
            fillcolor: category.color || "#D3D3D3",
            opacity: RIPENING_GRAPH_STYLES.categoryOpacity,
            line: {
                width: 0
            },
            layer: "below", // Ensure swimming lanes are drawn behind the graph lines
        }));
        // Add annotations for each category
        const annotations = categories.map((category) => ({
            x: 0,
            xref: "paper",
            y: isLogScale ? category.log_middle : category.middle,
            yref: "y",
            text: category.label,
            showarrow: false,
            font: {
                size: 12,
                color: "#4C4E52",
            },
            xanchor: "left",
            yanchor: "middle",
            position: "left"
        }));

        layout.shapes = swimmingLanes as any;
        layout.annotations = annotations as any;
        layout.yaxis = {
            ...layout.yaxis,
            // tickvals: categories.map((category) => parseFloat(category.upper)),
            // ticktext: categories.map((category) => category.upper),
            // tickmode: "array",
            // tickfont: {
            // size: 12,
            // color: "#4C4E52",
            // },

            visible: false, // * Hide y-axis if we have categories

        } as any;

        layout.margin = { l: 0, r: 0, t: 0, b: 50, pad: 0 };
        // * When categories are defined, show all categories in graph
        const category_low = Math.min(layout.yaxis.range[0], ...categories.map((i: any) => (isLogScale ? i.log_lower : i.lower)));
        const category_high = Math.max(layout.yaxis.range[1], ...categories.map((i: any) => (isLogScale ? i.log_upper : i.upper)));
        layout.yaxis.range = [category_low, category_high];

    }

    const hasData = sortedSeries?.some((serie) => serie?.length > 0) ?? false;

    return (
        <>

            {title && <h3 className="mb-2">{`${the_field?.label} Development`}</h3>}

            <div className="d-flex flex-wrap justify-content-start gap-2">
                {getFilledArrayOrDefault(fruit_config?.ripening_fields).length > 1 && <>
                    {fruit_config?.ripening_fields?.map((field, index) => <Button
                        key={index}
                        outline={index !== ripeningFieldIndex}
                        size="sm"
                        color="secondary"
                        onClick={() => setRipeningFieldIndex(index)}
                    >{field.label}</Button>)}
                </>}

                {checkLocations.length > 1 && <div className="ms-auto d-flex flex-wrap gap-2">
                    {checkLocations.map((item: any, index: number) => <Button
                        key={index}
                        outline={ignoreCheckLocations.includes(item?.value)}
                        size="sm"
                        color="secondary"
                        onClick={() => setIgnoreCheckLocations((prev: any) => {
                            if (prev.includes(item.value)) {
                                return prev.filter((i: any) => i !== item.value);
                            }
                            return [...prev, item.value];
                        })}
                        icon={faFilter}
                    >
                        {item.label}
                    </Button>)}
                </div>}
            </div>

            <div css={css`
                min-height: ${minHeight};
                .plotly .cursor-crosshair,
                .plotly .cursor-drag,
                .plotly .draglayer,
                .plotly .nsewdrag,
                .plotly .ewdrag,
                .plotly .nsdrag {
                    cursor: crosshair !important;
                }
            `} className="pt-2">
                {!hasData && <div className="text-center d-flex align-items-center justify-content-center" css={css`min-height: 24rem;background-color: #f8f8f8;`}>
                    <p className="text-muted mb-0">No data available<br></br>Start a check to track {the_field.label} progress</p>
                </div>}
                {hasData
                && <Plot
                    data={graphs as any}
                    useResizeHandler={true}
                    style={{ width: "100%", height: "100%", minHeight }}
                    layout={layout as any}
                    config={{
                        displayModeBar: false,
                        displaylogo: false,
                        responsive: true,
                        scrollZoom: false,
                        doubleClick: false,
                        doubleClickDelay: 0,
                    }}
                />}
            </div>
            {hasData && fruit_type !== "avocado" && <div className="pt-2">
                <Button
                    size="sm"
                    color="secondary"
                    outline={!isLogScale}
                    onClick={() => setIsLogScale((prev) => !prev)} // Toggle the scale
                >Log scale</Button>
            </div>}
        </>
    );
}

interface PredictionGraphData {
    lineX: string[];
    lineY: number[];
    bandX: string[];
    bandUpper: number[];
    bandLower: number[];
}

function buildPredictionGraphData({
    predictions,
    fieldname,
    lastActualTimestamp,
    lastActualValue,
    isLogScale = false,
}: {
    predictions: LocationPrediction[];
    fieldname: string;
    lastActualTimestamp?: string;
    lastActualValue?: number;
    isLogScale?: boolean;
}): PredictionGraphData | null {
    const sortedPredictions = [...predictions].sort(
        (a, b) => dayjs(a.ripening_date).valueOf() - dayjs(b.ripening_date).valueOf()
    );

    const lineX: string[] = [];
    const lineY: number[] = [];
    const bandX: string[] = [];
    const bandUpper: number[] = [];
    const bandLower: number[] = [];

    let hasPredictedPoint = false;

    // Handle last actual value - convert to log space if needed
    // Backend always sends linear values, so convert if log scale is enabled
    if (lastActualTimestamp && isValidNumber(lastActualValue)) {
        lineX.push(lastActualTimestamp);
        lineY.push(isLogScale ? toLogSpace(Number(lastActualValue)) : Number(lastActualValue));
    }

    sortedPredictions.forEach((prediction) => {
        const predictionMoment = dayjs(prediction.ripening_date);
        if (!predictionMoment.isValid()) {
            return;
        }

        // Backend always sends linear values, so get the value and convert if needed
        // Use the same fieldname as the actual values
        const linearValue = prediction.output?.[fieldname];
        if (!isValidNumber(linearValue)) {
            return;
        }

        hasPredictedPoint = true;
        const isoDate = toLocalGraphTime(prediction.ripening_date);
        const numericValue = isLogScale ? toLogSpace(Number(linearValue)) : Number(linearValue);
        lineX.push(isoDate);
        lineY.push(numericValue);

        // Backend always sends linear values for limits, so convert if needed
        const linearUpper = prediction.output?.upper_limit;
        const linearLower = prediction.output?.lower_limit;

        if (isValidNumber(linearUpper) && isValidNumber(linearLower)) {
            bandX.push(isoDate);
            bandUpper.push(isLogScale ? toLogSpace(Number(linearUpper)) : Number(linearUpper));
            bandLower.push(isLogScale ? toLogSpace(Number(linearLower)) : Number(linearLower));
        }
    });

    if (!hasPredictedPoint) {
        return null;
    }

    return {
        lineX,
        lineY,
        bandX,
        bandUpper,
        bandLower,
    };
}


function config_scatter_serie(created, values, color, name, locations, isLogScale = false, linearValuesForHover?: number[]) {
    return {
        type: "scatter",
        x: created,
        y: values.map(parseFloat),
        mode: "lines+points+markers",
        name,
        marker: {
            color,
            size: RIPENING_GRAPH_STYLES.marker.size,
        },
        line: {
            color,
            width: RIPENING_GRAPH_STYLES.lineWidth.actual,
        },
        hovertemplate: `<b>${name}</b> <b>%{customdata[0]}</b><br>`
                      + `Value: ${isLogScale ? "%{customdata[1]:.1f}" : "%{y:.2f}"}<br>`
                      + `Date: %{x}<br>`
                      + `<extra></extra>`,
        customdata: isLogScale && linearValuesForHover
            ? locations.map((loc: string, idx: number) => [loc, linearValuesForHover[idx]])
            : locations.map((loc: string) => [loc]),
    };
}
